import React, { useState } from 'react';

import { makeStyles } from '@material-ui/styles';
import { Typography } from '@material-ui/core';
import { ToggleButtonGroup, ToggleButton } from '@material-ui/lab';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import { useOrganizations, useUsers, useSettings } from 'hooks';
import { Card, CardContent } from '@material-ui/core';

import { Page } from 'components';
import { UsersPage } from './components/Users';
import UsersPropertiesPage from './components/UsersProperties/UsersPropertiesPage';

import OrganizationsPage from './components/Organizations/OrganizationsPage';
import { Roles } from 'utils/authHelpers';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2),
    width: '100%',
    height: '100%',
    minHeight: '100%',
    flex: 1,
  },
  toggleButtonGroup: {
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      width: '100%',
    },
  },
  toggleButton: {
    minWidth: 100,
    [theme.breakpoints.down('sm')]: {
      minWidth: '33%',
    },
  },
  toggleLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(2),
  },
  cardHeader: {
    padding: '0',
    zIndex: 200,
    display: 'flex',
    marginBottom: theme.spacing(3),
  },
  picker: {
    marginTop: theme.spacing(1),
  },
  toggles: {
    display: 'flex',
  },
}));

const AdminPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const {
    fetchUsers,
    setUserOrgName,
    users,
    filteredUsersByOrg,
    isFetchingUsers,
    filterByOrg,
    setFilterByOrg,
    selectedUser,
    setSelectedUser,
    saveUser,
    userInviteRequest,
  } = useUsers();

  const {
    organizations,
    selectedOrganization,
    selectedOrganizationId,
    fetchOrganizations,
    isFetchingOrganizations,
    setSelectedOrganization,
    saveOrganization,
  } = useOrganizations();

  const [pageView, setAdminView] = useState(_.includes(currentUserRoles, Roles.Super) ? 'orgs' : 'users');
  const [orgList] = useState(_.includes(currentUserRoles, Roles.Super) ? fetchOrganizations : []);
  const { currentUserRoles, instancePermissions, instanceRoles, isSuper } = useSettings();
  const title = '';

  const handlePageViewChange = (event, newValue) => {
    if (newValue !== null) {
      setAdminView(newValue);
    }
    if (!users && newValue === 'users' && selectedOrganizationId) {
      if (selectedOrganization) {
        setFilterByOrg(selectedOrganization.name);
      }
      fetchUsers(selectedOrganizationId);
    }
    if (!users && newValue === 'usersProps' && selectedOrganizationId) {
      if (selectedOrganization) {
        setFilterByOrg(selectedOrganization.name);
      }
      fetchUsers(selectedOrganizationId);
    }
    if (newValue === 'orgs' && _.includes(currentUserRoles, Roles.Super)) {
      fetchOrganizations();
    }
  };

  return (
    <Page title={t(title)} className={classes.root}>
      <ToggleButtonGroup
        value={pageView}
        exclusive
        className={classes.toggleButtonGroup}
        onChange={handlePageViewChange}
        aria-label="Admin View"
      >
        {_.includes(currentUserRoles, Roles.Super) && (
          <ToggleButton className={classes.toggleButton} value="orgs" aria-label={t('orgs')}>
            <Typography className={classes.toggleLabel}>{t('orgs')}</Typography>
          </ToggleButton>
        )}
        <ToggleButton className={classes.toggleButton} value="users" aria-label={t('users')}>
          <Typography className={classes.toggleLabel}>{t('users')}</Typography>
        </ToggleButton>
        <ToggleButton className={classes.toggleButton} value="usersProps" aria-label={t('usersProps')}>
          <Typography className={classes.toggleLabel}>{t('usersProps')}</Typography>
        </ToggleButton>
      </ToggleButtonGroup>
      <Card>
        <CardContent style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {pageView === 'orgs' && _.includes(currentUserRoles, Roles.Super) && (
            <OrganizationsPage
              organizations={organizations ? organizations : orgList}
              isFetchingOrganizations={isFetchingOrganizations}
              setSelectedOrganization={setSelectedOrganization}
              saveOrganization={saveOrganization}
              fetchOrganizations={fetchOrganizations}
            />
          )}
          {pageView === 'users' && (
            <UsersPage
              setUserOrgName={setUserOrgName}
              fetchUsers={fetchUsers}
              setFilterByOrg={setFilterByOrg}
              instancePermissions={instancePermissions}
              instanceRoles={instanceRoles}
              users={filteredUsersByOrg?.length > 0 ? filteredUsersByOrg : users}
              isFetchingUsers={isFetchingUsers}
              organizations={organizations}
              filterByOrg={filterByOrg}
              selectedOrganization={selectedOrganization ? selectedOrganization : { id: selectedOrganizationId }}
              currentUserRoles={currentUserRoles}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              saveUser={saveUser}
              inviteUser={userInviteRequest}
              isSuper={isSuper}
            />
          )}

          {pageView === 'usersProps' && (
            <UsersPropertiesPage
              setUserOrgName={setUserOrgName}
              fetchUsers={fetchUsers}
              setFilterByOrg={setFilterByOrg}
              instancePermissions={instancePermissions}
              instanceRoles={instanceRoles}
              users={filteredUsersByOrg?.length > 0 ? filteredUsersByOrg : users}
              isFetchingUsers={isFetchingUsers}
              organizations={organizations}
              filterByOrg={filterByOrg}
              selectedOrganization={selectedOrganization ? selectedOrganization : { id: selectedOrganizationId }}
              currentUserRoles={currentUserRoles}
              selectedUser={selectedUser}
              setSelectedUser={setSelectedUser}
              saveUser={saveUser}
              inviteUser={userInviteRequest}
              isSuper={isSuper}
            />
          )}
        </CardContent>
      </Card>
    </Page>
  );
};

export default AdminPage;
